import {
  requireSelectedSoundtrackId,
  selectedSoundtrackConfigs,
  type SelectedSoundtrackId,
} from "../config/soundtrack-config";
import type { ProgressMutation } from "../application/progress-mutation";

export type SoundtrackSelectionHandler = (
  id: SelectedSoundtrackId,
) => ProgressMutation<boolean>;

export const soundtrackSelectAccessibleName = (
  id: SelectedSoundtrackId,
): string => `Soundtrack. ${selectedSoundtrackConfigs[id].name} selected.`;

export class SoundtrackSelect {
  private selectedId: SelectedSoundtrackId;

  public constructor(
    private readonly element: HTMLSelectElement,
    initialId: SelectedSoundtrackId,
    private readonly select: SoundtrackSelectionHandler,
  ) {
    this.selectedId = initialId;
    element.addEventListener("change", this.handleChange);
    this.render(initialId);
  }

  public destroy(): void {
    this.element.removeEventListener("change", this.handleChange);
  }

  public render(id: SelectedSoundtrackId): void {
    this.selectedId = id;
    this.element.value = id;
    this.element.setAttribute("aria-label", soundtrackSelectAccessibleName(id));
  }

  private readonly handleChange = (): void => {
    const selectedId = requireSelectedSoundtrackId(this.element.value);
    const selected = this.select(selectedId);
    if (selected instanceof Promise) {
      this.element.disabled = true;
      void selected.then(
        (durable) => {
          this.element.disabled = false;
          if (durable) {
            this.render(selectedId);
          } else {
            this.render(this.selectedId);
          }
        },
        () => {
          this.element.disabled = false;
          this.render(this.selectedId);
        },
      );
      return;
    }
    if (!selected) {
      this.render(this.selectedId);
      return;
    }
    this.render(selectedId);
  };
}
